import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { FallbackCallout } from '@bingads-webui-react/fallback-feature';
import { withRouter } from '@bingads-webui-react/router';
import { getAccountsDropdownMenuItemStyles } from '../../styles';
import { BaMenuItem } from '../toolbar/menu-item';
import { OverflowBreadcrumb } from './overflow-breadcrumb';
import { withCustomerUrl, withAccountUrl } from './utilities';

export const getOldCMUIPath = (cid, uid) => `/Campaign/AccountsSummary?cid=${cid}&uid=${uid}`;

export const AccountsDropdownMenuItem =
  withRouter(withScope(withDefaultStyles(BaMenuItem, getAccountsDropdownMenuItemStyles)));

const CustomerMenuItemInternal = ({
  scope, name, number, customerUrl, cid, nodeAriaLabelledby,
}) => {
  const { uid } = scope.values;
  const nodeId = `customer-menu-${cid}`;

  const renderNode = !customerUrl ? (
    <AccountsDropdownMenuItem
      className="customer-node-with-fall-back"
      href="#"
      menuItemKey={cid}
      getOldUIPathOverride={() => getOldCMUIPath(cid, uid)}
      tooltipPlacement="top"
      siteMap={[]}
    >
      {name}
    </AccountsDropdownMenuItem>
  ) : (
    <a href={customerUrl} aria-labelledby={`${nodeAriaLabelledby} ${nodeId}`}>
      <OverflowBreadcrumb
        text={name}
        customClassName="customer-node-with-fall-back"
        role="menuitem"
        id={nodeId}
      />
    </a>
  );

  return (
    <div className="tree-node-content">
      {renderNode}
      <span className="id-span">
        {number}
      </span>
    </div>
  );
};

CustomerMenuItemInternal.propTypes = {
  cid: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  number: PropTypes.string.isRequired,
  customerUrl: PropTypes.string,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  nodeAriaLabelledby: PropTypes.string,
};

CustomerMenuItemInternal.defaultProps = {
  customerUrl: null,
  nodeAriaLabelledby: undefined,
};

export const CustomerMenuItem = withCustomerUrl(withScope(React.memo(CustomerMenuItemInternal)));

const CustomerBreadcrumbItemInternal = ({
  name, scope, customerUrl, classes,
}) => {
  const { cid, uid } = scope.values;

  return !customerUrl ? (
    <FallbackCallout
      title={name}
      placement="right"
      getOldUIPathOverride={() => getOldCMUIPath(cid, uid)}
    >
      <span className={classes}>{name}</span>
    </FallbackCallout>
  ) : (
    <a className={classes} href={customerUrl}>{name}</a>
  );
};

CustomerBreadcrumbItemInternal.propTypes = {
  name: PropTypes.string.isRequired,
  customerUrl: PropTypes.string,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  classes: PropTypes.arrayOf(PropTypes.string),
};

CustomerBreadcrumbItemInternal.defaultProps = {
  customerUrl: null,
  classes: [],
};

export const CustomerBreadcrumbItem =
  withCustomerUrl(withScope(React.memo(CustomerBreadcrumbItemInternal)));

const AccountMenuItemInternal = ({
  name, number, statusText, accountUrl, i18n, nodeAriaLabelledby, aid,
}) => {
  const nodeId = `account-menu-${aid}`;

  return (
    <a className="tree-node-content" href={accountUrl} aria-labelledby={`${nodeAriaLabelledby} ${nodeId}`}>
      <OverflowBreadcrumb
        text={statusText ? `${name} (${i18n.getString(statusText)})` : name}
        customClassName={
          statusText
            ? classNames(['text-span', 'gray-out-text-span'])
            : 'text-span'
        }
        role="menuitem"
        id={nodeId}
      />
      <span className="id-span">
        {number}
      </span>
    </a>
  );
}

AccountMenuItemInternal.propTypes = {
  name: PropTypes.string.isRequired,
  number: PropTypes.string.isRequired,
  statusText: PropTypes.string,
  accountUrl: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  nodeAriaLabelledby: PropTypes.string,
};

AccountMenuItemInternal.defaultProps = {
  statusText: null,
  nodeAriaLabelledby: undefined,
};

export const AccountMenuItem =
  withAccountUrl(withI18nConsumer(React.memo(AccountMenuItemInternal)));
